// Third party
import React from 'react';

// Components
import BMOGIFComparisonScenario from './BMOGIFComparisonScenario';
import EditBMOGIFComparisonScenario from './EditBMOGIFComparisonScenario';
import { GenericEmptyState } from '../../../components/misc';

// Utils
import { BMOi18n } from '../../../../utils/bmo-gif/constants';
import { getTotalGifPortfolioAllocation } from '../../../../utils/bmo-gif/index';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';

type StateProps = {
  item: BMOGIFItem;
  locale: string;
  editedScenario?: BMOGIFScenario;
  portfolio?: BMOGIFPortfolio;
  readOnly?: boolean;
  selectedScenario?: BMOGIFScenario;
  showGraph: boolean;
};

type DispatchProps = {
  createNewScenario: () => void;
  cancelScenarioEditing: () => void;
  saveScenario: () => void;
  setLumpSumWithdrawalScheme: (scheme: string) => void;
  setLumpSumWithdrawalAmount: (amount: number) => void;
  setLumpSumWithdrawalAt: (at: number) => void;
  setSystematicWithdrawalAmount: (amount: number) => void;
  setSystematicWithdrawalType: (type: string) => void;
  setSystematicWithdrawalScheme: (scheme: string) => void;
  setSystematicWithdrawalStart: (start?: number) => void;
  setSystematicWithdrawalEnd: (end?: number) => void;
  setSystematicWithdrawalYears: (years?: number) => void;
  setSystematicWithdrawalFrequency: (frequency: string) => void;
  loadScenarioForEdit: (scenario: BMOGIFScenario) => void;
  deleteScenario: (scenario: BMOGIFScenario) => void;
  selectScenario: (scenario: BMOGIFScenario) => void;
  setLumpSumMonth: (month?: number) => void;
  setSystematicWithdrawalMonthEnd: (month?: number) => void;
  setSystematicWithdrawalMonthStart: (month?: number) => void;
};

type Props = { accountType?: string } & StateProps & DispatchProps;

class BMOGIFComparisonCard extends React.Component<Props> {
  scenarioIsBeingEdited = (scenario: BMOGIFScenario) => !!this.props.editedScenario && this.props.editedScenario._id === scenario._id;

  scenarioIsSelected = (scenario: BMOGIFScenario) => {
    const { selectedScenario } = this.props;

    return !!selectedScenario && selectedScenario._id === scenario._id;
  };

  isPortfolioFull = () => {
    const { portfolio } = this.props;

    return !!portfolio && getTotalGifPortfolioAllocation(portfolio) === 100;
  };

  renderWithdrawalScenarios = () => {
    const { item, locale, editedScenario, portfolio, readOnly, showGraph, accountType } = this.props;

    if (!item.scenarios || !item.scenarios.length) {
      return (
        <div className="withdrawal-scenario">
          <GenericEmptyState
            header={BMOi18n[locale].SCENARIOS.WITHDRAWAL_SCENARIOS}
            headerIcon="icon-info"
            body={BMOi18n[locale].SCENARIOS.EMPTY_WITHDRAWAL_SCENARIOS}
          />
        </div>
      );
    }

    return (
      <>
        {item.scenarios.map((scenario: BMOGIFScenario, i) => {
          const isEdited = this.scenarioIsBeingEdited(scenario);

          if (isEdited && editedScenario && portfolio) {
            return (
              <React.Fragment key={scenario._id}>
                <div className="withdrawal-scenario editing-mode">
                  <EditBMOGIFComparisonScenario
                    accountType={accountType}
                    scenario={editedScenario}
                    scenarioNumber={i + 1}
                    locale={locale}
                    portfolio={portfolio}
                    saveScenario={this.props.saveScenario}
                    cancelScenarioEditing={this.props.cancelScenarioEditing}
                    setLumpSumWithdrawalScheme={this.props.setLumpSumWithdrawalScheme}
                    setLumpSumWithdrawalAmount={this.props.setLumpSumWithdrawalAmount}
                    setLumpSumWithdrawalAt={this.props.setLumpSumWithdrawalAt}
                    setSystematicWithdrawalAmount={this.props.setSystematicWithdrawalAmount}
                    setSystematicWithdrawalType={this.props.setSystematicWithdrawalType}
                    setSystematicWithdrawalScheme={this.props.setSystematicWithdrawalScheme}
                    setSystematicWithdrawalStart={this.props.setSystematicWithdrawalStart}
                    setSystematicWithdrawalEnd={this.props.setSystematicWithdrawalEnd}
                    setSystematicWithdrawalYears={this.props.setSystematicWithdrawalYears}
                    setSystematicWithdrawalFrequency={this.props.setSystematicWithdrawalFrequency}
                    setLumpSumMonth={this.props.setLumpSumMonth}
                    setSystematicWithdrawalMonthEnd={this.props.setSystematicWithdrawalMonthEnd}
                    setSystematicWithdrawalMonthStart={this.props.setSystematicWithdrawalMonthStart}
                  />
                </div>
              </React.Fragment>
            );
          }

          return (
            <BMOGIFComparisonScenario
              key={scenario._id}
              className="withdrawal-scenario"
              item={item}
              scenario={scenario}
              locale={locale}
              scenarioNumber={i + 1}
              loadScenarioForEdit={this.props.loadScenarioForEdit}
              deleteScenario={this.props.deleteScenario}
              selectScenario={this.props.selectScenario}
              selected={this.scenarioIsSelected(scenario)}
              portfolioIsFull={this.isPortfolioFull()}
              readOnly={readOnly}
              showGraph={showGraph}
            />
          );
        })}
      </>
    );
  };

  render() {
    const { item, locale, readOnly, showGraph } = this.props;

    return (
      <div className="bmo-gif-comparison-card">
        <BMOGIFComparisonScenario
          className="base-scenario"
          key={item.baseScenario._id}
          item={item}
          scenario={item.baseScenario}
          locale={locale}
          createNewScenario={this.props.createNewScenario}
          selectScenario={this.props.selectScenario}
          selected={this.scenarioIsSelected(item.baseScenario)}
          portfolioIsFull={this.isPortfolioFull()}
          readOnly={readOnly}
          showGraph={showGraph}
        />

        {this.renderWithdrawalScenarios()}
      </div>
    );
  }
}

export default BMOGIFComparisonCard;
